import { AnimatePresence, motion } from "framer-motion";
import { Copy, X, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

export default function ExitOfferPopup() {
  const { exitOfferOpen, setExitOfferOpen, setPromo } = useCart();
  const navigate = useNavigate();

  const claim = () => {
    setPromo({ code: "VOLTAIC20", percent: 20, kind: "site" });
    setExitOfferOpen(false);
    toast.success("VOLTAIC20 applied — 20% off waiting at checkout");
    navigate("/checkout");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText("VOLTAIC20");
      toast.success("Code copied");
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <AnimatePresence>
      {exitOfferOpen && (
        <motion.div
          key="exit-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setExitOfferOpen(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="exit-offer-modal"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-voltaic-cyan/40 bg-voltaic-surface p-8 text-center shadow-[0_0_80px_rgba(0,240,255,0.15)]"
          >
            <button
              data-testid="exit-offer-close"
              onClick={() => setExitOfferOpen(false)}
              aria-label="Close offer"
              className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full border border-voltaic-line text-voltaic-muted transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
            >
              <X size={15} />
            </button>
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-voltaic-cyan/10 text-voltaic-cyan">
              <Zap size={24} />
            </span>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-voltaic-cyan">
              Don't drop the charge
            </p>
            <h2 className="mt-2 font-display text-2xl font-extrabold uppercase leading-tight">
              Take 20% off before you go
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-voltaic-muted">
              Your cart is still loaded. Apply this code and finish in under a minute —
              free shipping over $999.
            </p>
            <button
              data-testid="exit-offer-copy"
              onClick={copy}
              className="mx-auto mt-5 flex items-center gap-2 rounded-full border border-dashed border-voltaic-cyan/60 bg-voltaic-cyan/5 px-6 py-2.5 font-mono text-sm font-bold tracking-[0.25em] text-voltaic-cyan transition-colors hover:bg-voltaic-cyan/10"
            >
              VOLTAIC20 <Copy size={13} />
            </button>
            <button
              data-testid="exit-offer-claim"
              onClick={claim}
              className="mt-5 w-full rounded-full bg-voltaic-cyan py-3.5 font-display font-bold uppercase tracking-wide text-voltaic-bg transition-shadow hover:glow-cyan"
            >
              Claim 20% & finish checkout
            </button>
            <button
              data-testid="exit-offer-dismiss"
              onClick={() => setExitOfferOpen(false)}
              className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted transition-colors hover:text-voltaic-ink"
            >
              No thanks
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
